/** Four stepping stones anchor the entrance. Forest planting lives in SceneForest. */
import { useEffect, useMemo } from "react";
import * as THREE from "three";

interface SceneDressingProps {
	enabled: boolean;
	approachEdgeZ: number;
	groundY: number;
}
const STONE_COUNT = 4;
const STONE_WIDTH_M = 0.5;
const STONE_DEPTH_M = 0.4;
const STONE_THICKNESS_M = 0.04;
const STONE_GAP_M = 0.7;

interface SteppingStonesProps {
	approachEdgeZ: number;
	groundY: number;
}

function SteppingStones({ approachEdgeZ, groundY }: SteppingStonesProps) {
	// Stones lead away from the entry approach (front of the bottom
	// stair, or the front edge of a deck if one is attached). A
	// future pass can read the actual entry-door wall from `openings`.
	const geometry = useMemo(
		() =>
			new THREE.BoxGeometry(STONE_WIDTH_M, STONE_THICKNESS_M, STONE_DEPTH_M),
		[],
	);
	const material = useMemo(
		() =>
			new THREE.MeshStandardMaterial({
				color: 0x9b9994,
				roughness: 0.92,
				metalness: 0,
				envMapIntensity: 0.4,
			}),
		[],
	);

	useEffect(
		() => () => {
			geometry.dispose();
		},
		[geometry],
	);
	useEffect(
		() => () => {
			material.dispose();
		},
		[material],
	);

	const positions = useMemo(() => {
		// First stone clears the approach edge by half a stride so it
		// reads as a separate path. Stagger ±6 cm off-axis so the
		// stones don't look like railroad ties.
		const STONE_FIRST_GAP_M = 0.45;
		const startZ = approachEdgeZ + STONE_FIRST_GAP_M + STONE_DEPTH_M / 2;
		const out: Array<[number, number, number]> = [];
		for (let i = 0; i < STONE_COUNT; i += 1) {
			const z = startZ + i * STONE_GAP_M;
			const x = (i % 2 === 0 ? -1 : 1) * 0.06;
			out.push([x, groundY + STONE_THICKNESS_M / 2 + 0.005, z]);
		}
		return out;
	}, [approachEdgeZ, groundY]);

	return (
		<group name="dressing-stones">
			{positions.map((pos, i) => (
				<mesh
					// biome-ignore lint/suspicious/noArrayIndexKey: stable order, deterministic count
					key={i}
					position={pos}
					geometry={geometry}
					material={material}
					castShadow={false}
					receiveShadow
				/>
			))}
		</group>
	);
}

export function SceneDressing({
	enabled,
	approachEdgeZ,
	groundY,
}: SceneDressingProps) {
	return enabled ? (
		<SteppingStones approachEdgeZ={approachEdgeZ} groundY={groundY} />
	) : null;
}
